import {
  LoginlessConversationResponse,
  SendLoginlessMessageResponse,
} from "./types";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { captureException } from "utils/sentry";

/**
 * Fetch the initial conversation data for a loginless weekly check-in session.
 * Always calls Laravel — frontend feature flags handle subsequent message routing.
 * @param token - The encrypted thread token from the URL
 * @returns Promise with conversation data (including companyAccountId for ConfigCat init) or error information
 */
export async function getLoginlessConversation(
  token: string
): Promise<LoginlessConversationResponse> {
  try {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weekly-checkin/${token}`;

    // Use fetch directly instead of the `request` utility — loginless users
    // have no JWT, and the `request` helper sends `Authorization: null` which
    // causes the backend to return 401.  Authentication here is via the
    // encrypted thread token in the URL.
    const response = await fetch(requestUrl, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      // Try to parse error body for threadId and specific error messages
      let errorData: { error?: string; threadId?: string } = {};
      try {
        errorData = await response.json();
      } catch {
        // If we can't parse, errorData stays empty
      }
      const threadId: string | undefined = errorData.threadId;

      if (response.status === 401) {
        return {
          success: false,
          error: "Token has expired",
          errorCode: "TOKEN_EXPIRED",
          threadId,
        };
      }

      if (response.status === 400) {
        return {
          success: false,
          error: "Invalid token",
          errorCode: "INVALID_TOKEN",
          threadId,
        };
      }

      if (response.status === 404 || response.status === 500) {
        return {
          success: false,
          error: "Server error",
          errorCode: "SERVER_ERROR",
          threadId,
        };
      }

      if (errorData.error === "Checkin already completed") {
        return {
          success: false,
          error: "Check-in already completed",
          errorCode: "CHECKIN_COMPLETED",
          threadId: errorData.threadId,
        };
      }
      if (errorData.error === "Message limit reached") {
        return {
          success: false,
          error: "Message limit reached",
          errorCode: "MESSAGE_LIMIT_REACHED",
          threadId: errorData.threadId,
        };
      }

      return {
        success: false,
        error: "Server error",
        errorCode: "SERVER_ERROR",
        threadId,
      };
    }

    const data = await response.json();

    return {
      success: true,
      data: data,
    };
  } catch {
    return {
      success: false,
      error: "Network error occurred while loading conversation",
    };
  }
}

/**
 * Send a message in a loginless weekly check-in session
 * @param token - The encrypted thread token
 * @param messages - Array of all messages in the conversation
 * @returns Promise with the AI response or error information
 */
export async function sendLoginlessMessage(
  token: string,
  messages: Array<{ role: "user" | "assistant"; content: string }>
): Promise<SendLoginlessMessageResponse> {
  try {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/chatbot/loginless`;

    // Use fetch directly — loginless users have no JWT.
    // Authentication is via the encrypted thread token in the body.
    const response = await fetch(requestUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        token,
        messages,
      }),
    });

    if (!response.ok) {
      let errorData: { error?: string; threadId?: string } = {};
      try {
        errorData = await response.json();
      } catch {
        // If we can't parse, errorData stays empty
      }
      const threadId: string | undefined = errorData.threadId;

      if (response.status === 401) {
        return {
          success: false,
          error: "Token has expired",
          errorCode: "TOKEN_EXPIRED",
          threadId,
        };
      }

      if (response.status === 400) {
        return {
          success: false,
          error: "Invalid token",
          errorCode: "INVALID_TOKEN",
          threadId,
        };
      }

      if (response.status === 429) {
        return {
          success: false,
          error: "Message limit exceeded",
          errorCode: "MESSAGE_LIMIT_REACHED",
          threadId,
        };
      }

      if (response.status === 404 || response.status === 500) {
        return {
          success: false,
          error: "Server error",
          errorCode: "SERVER_ERROR",
          threadId,
        };
      }

      if (
        errorData.error === "Checkin already completed" ||
        errorData.error === "Check-in complete"
      ) {
        return {
          success: false,
          error: "Check-in already completed",
          errorCode: "CHECKIN_COMPLETED",
          threadId: errorData.threadId,
        };
      }

      return {
        success: false,
        error: "Server error",
        errorCode: "SERVER_ERROR",
        threadId,
      };
    }

    const data = await response.json();

    return {
      success: true,
      data: data,
    };
  } catch (error) {
    captureException(error instanceof Error ? error : new Error(String(error)));
    return {
      success: false,
      error: "Network error occurred while sending message",
    };
  }
}
